import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import { useAuth } from '../../context/AuthContext';
import OrderDetail from '../customerProfile/OrderDetail';
import OrderService from '../order/OrderService';
import SheetImportService from './SheetImportService';

// Warehouse receiving sheet → weekly_ships, every day at 06:00 (backend scheduler).
// Shows each shop's sheet and last run, and the problems someone has to fix.
// Sheet links and "pull now" are superuser only (the backend enforces it too).

const SHOP_LABEL = { smile: 'Smile', tama: 'Tama' };
const POLL_MS = 5000;
// Same look as the order popup on the customer profile page.
const MODAL_STYLE = {
  overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1000 },
  content: {
    top: '5%', left: '50%', right: 'auto', bottom: 'auto', width: '1100px', maxWidth: '95vw', maxHeight: '90vh',
    transform: 'translateX(-50%)', overflow: 'auto',
  },
};

const errorMessage = (err) => err?.response?.data?.message || err?.message || 'เกิดข้อผิดพลาด';
const formatTime = (value) => (value ? new Date(value).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' }) : '-');

// One line per shop from the stored run result: { shop, summary: [tab…] } | { shop, error }.
const summarize = (lastResult) => {
  if (!lastResult) return null;
  if (lastResult.error) return { error: lastResult.error };
  const tabs = lastResult.summary || [];
  const sum = (key) => tabs.reduce((n, t) => n + (t[key] || 0), 0);
  return {
    imported: tabs.filter((t) => t.shipDateId && !t.skippedReason && !t.error).length,
    created: sum('created'),
    updated: sum('updated'),
    photos: sum('photosQueued'),
    photosLater: sum('photosDeferred'),
  };
};

const SheetImportPanel = () => {
  const { authState } = useAuth();
  const isSuperuser = authState?.user?.role === 'superuser';
  const shopName = useSelector((state) => state.shop.name);

  const [status, setStatus] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState('');
  const [openedOrder, setOpenedOrder] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  // Only the latest load may update the screen: a refresh started before "เสร็จแล้ว" would
  // otherwise land after it and bring the closed problem back. → data | null (failed) |
  // undefined (overtaken by a newer load).
  const loadSeq = useRef(0);
  const load = useCallback(async ({ quiet = false } = {}) => {
    const mine = ++loadSeq.current;
    try {
      const { data } = await SheetImportService.getStatus();
      if (mine !== loadSeq.current) return undefined;
      setStatus(data);
      return data;
    } catch (err) {
      if (mine !== loadSeq.current) return undefined;
      if (!quiet) toast.error(errorMessage(err));
      return null;
    }
  }, []);

  // While a run is going, check every few seconds; stop when it ends or the check fails.
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const data = await load({ quiet: true });
      if (data === null || (data && !data.running)) stopPolling(); // undefined = overtaken, keep polling
    }, POLL_MS);
  }, [load]);

  useEffect(() => {
    load().then((data) => data?.running && startPolling());
    return stopPolling;
  }, [load, startPolling]);

  const saveSheet = async (shop) => {
    setBusy(`save-${shop}`);
    try {
      await SheetImportService.saveSheet(shop, drafts[shop] ?? '');
      toast.success(`บันทึกชีท ${SHOP_LABEL[shop]} แล้ว`);
      setDrafts((d) => ({ ...d, [shop]: undefined }));
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy('');
    }
  };

  const runNow = async () => {
    setBusy('run');
    try {
      await SheetImportService.runNow();
      toast.info('เริ่มนำเข้าแล้ว อาจใช้เวลาหลายนาที');
      setStatus((s) => (s ? { ...s, running: true } : s));
      startPolling();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy('');
    }
  };

  // The same order edit form as the customer profile page, so the weight can be fixed here.
  const openOrder = async (o) => {
    setBusy(`order-${o.id}`);
    try {
      const { data } = await OrderService.getOrderByUuid(o.uuid, o.shop);
      setOpenedOrder(data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy('');
    }
  };
  const closeOrder = () => setOpenedOrder(null);
  const orderSaved = () => {
    closeOrder();
    load({ quiet: true }); // refresh the weights shown on the order buttons
  };

  // resolution: 'done' (fixed it) | 'dropped' (nothing to fix) — both close it, who/when recorded.
  const resolve = async (id, resolution) => {
    setBusy(`issue-${id}`);
    try {
      await SheetImportService.resolveIssue(id, resolution);
      setStatus((s) => ({ ...s, issues: s.issues.filter((i) => i.id !== id) }));
      load({ quiet: true }); // newest load: drops any older refresh still on its way
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy('');
    }
  };

  if (!status) return null;
  const { running, sources, issues } = status;

  return (
    <div className="my-4 rounded-md border border-gray-300 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold">นำเข้าชีทโกดังอัตโนมัติ — รอบเรือเท่านั้น (ทุกวัน 06:00)</h3>
        {isSuperuser && (
          <button
            onClick={runNow}
            disabled={running || busy === 'run'}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-white disabled:opacity-50"
          >
            {running ? 'กำลังนำเข้า…' : 'ดึงข้อมูลตอนนี้'}
          </button>
        )}
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {sources.map((s) => {
          const result = summarize(s.lastResult);
          return (
            <div key={s.shop} className="rounded-md bg-gray-50 p-3 text-sm">
              <div className="font-semibold">{SHOP_LABEL[s.shop]}</div>
              <div className="mt-1">
                ชีท:{' '}
                {s.spreadsheetId ? (
                  <a
                    href={`https://docs.google.com/spreadsheets/d/${s.spreadsheetId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline"
                  >
                    เปิดชีท
                  </a>
                ) : (
                  <span className="text-red-600">ยังไม่ได้ตั้งค่า (ไม่นำเข้า)</span>
                )}
              </div>
              <div>นำเข้าล่าสุด: {formatTime(s.lastRunAt)}</div>
              {result?.error && <div className="text-red-600">อ่านชีทไม่ได้: {result.error}</div>}
              {result && !result.error && (
                <div className="text-gray-700">
                  {result.imported} รอบ · กล่องใหม่ {result.created} · อัปเดต {result.updated} · รูป {result.photos}
                  {result.photosLater > 0 && ` · รูปรอรอบหน้า ${result.photosLater}`}
                </div>
              )}
              {isSuperuser && (
                <div className="mt-2 flex gap-2">
                  <input
                    className="flex-1 rounded border border-gray-300 px-2 py-1"
                    placeholder="วางลิงก์ Google Sheet หรือ ID (เว้นว่าง = ปิด)"
                    value={drafts[s.shop] ?? s.spreadsheetId ?? ''}
                    onChange={(e) => setDrafts((d) => ({ ...d, [s.shop]: e.target.value }))}
                  />
                  <button
                    onClick={() => saveSheet(s.shop)}
                    disabled={drafts[s.shop] === undefined || busy === `save-${s.shop}`}
                    className="rounded-md bg-green-600 px-3 py-1 text-white disabled:opacity-50"
                  >
                    บันทึก
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <div className="font-semibold">ปัญหาที่ต้องดู ({issues.length})</div>
        {issues.length === 0 ? (
          <div className="text-sm text-gray-500">ไม่มี</div>
        ) : (
          <ul className="mt-1 divide-y text-sm">
            {issues.map((i) => (
              <li key={i.id} className="flex items-start justify-between gap-3 py-2">
                <div>
                  <div>{i.message}</div>
                  {(i.trackingJp || i.orders?.length > 0) && (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {i.trackingJp && <span className="text-gray-700">เลขพัสดุ {i.trackingJp}</span>}
                      {i.orders?.map((o) => (o.shop === shopName ? (
                        <button
                          key={o.id}
                          onClick={() => openOrder(o)}
                          disabled={busy === `order-${o.id}`}
                          className="rounded border border-blue-500 px-2 py-0.5 text-blue-600 disabled:opacity-50"
                        >
                          ออเดอร์ #{o.id} {o.smileNameInput} ({o.weight ?? '-'} kg)
                        </button>
                      ) : (
                        // The order form saves with the shop picked in the top bar, so an order of the
                        // other shop can't be saved from here ("Order not found").
                        <span key={o.id} className="text-gray-500">
                          ออเดอร์ #{o.id} ({o.weight ?? '-'} kg) — สลับเป็นร้าน {SHOP_LABEL[o.shop] || o.shop} เพื่อแก้
                        </span>
                      )))}
                    </div>
                  )}
                  <div className="text-xs text-gray-500">พบครั้งแรก {formatTime(i.firstSeenAt)}</div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => resolve(i.id, 'done')}
                    disabled={busy === `issue-${i.id}`}
                    className="rounded-md border border-gray-400 px-2 py-1 disabled:opacity-50"
                  >
                    เสร็จแล้ว
                  </button>
                  <button
                    onClick={() => resolve(i.id, 'dropped')}
                    disabled={busy === `issue-${i.id}`}
                    className="rounded-md border border-gray-300 px-2 py-1 text-gray-500 disabled:opacity-50"
                  >
                    ไม่ต้องทำ
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Modal isOpen={!!openedOrder} onRequestClose={closeOrder} style={MODAL_STYLE} ariaHideApp={false}>
        {openedOrder && <OrderDetail order={openedOrder} handleSuccessUpdate={orderSaved} />}
      </Modal>
    </div>
  );
};

export default SheetImportPanel;
